import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

export const VENDOIQ_PALETTE = {
  navy:      '#191D5A',
  navyDeep:  '#0F1240',
  cream:     '#F7F7F2',
  mint:      '#62E194',
  mintDark:  '#61C554',
  mintBg:    '#ECFFF3',
  gold:      '#F6BD55',
  coral:     '#EE6A5F',
  link:      '#0070C0',
  // Brand blue brightened for dark mode: navy (#191D5A) is too dark to read as
  // a foreground on dark surfaces, so dark-mode accents use this instead.
  linkBright: '#4096FF',
  slate:     '#4A4F6A',
} as const;

export const brandColors = {
  honeydew:       '#ECFFF3',
  midnightNavy:   '#191D5A',
  checkmarkGreen: '#62E194',
  signatureGold:  '#F6BD55',
  goldHighlight:  '#FCE0B4',

  pillLead:       '#9CA3AF',
  pillContact:    '#3B82F6',
  pillCustomer:   '#62E194',
  pillExCustomer: '#EF4444',
  pillPartner:    '#A855F7',
  pillCompetitor: '#F97316',
} as const;

/**
 * Fixed chrome colors for the brand-navy nav rail (sider + top header). The
 * rail is navy in BOTH light and dark mode, so its text is always light-on-dark
 * — these are deliberately theme-INDEPENDENT, unlike the `colorText*` tokens.
 * Replaces bare `#fff` / `rgba(255,255,255,…)` literals in AdminLayout,
 * ThemeToggle, and the console tenant picker.
 */
export const chrome = {
  onNavy:       '#FFFFFF',
  onNavyStrong: 'rgba(255, 255, 255, 0.85)',
  onNavyMuted:  'rgba(255, 255, 255, 0.65)',
  /** Hover tint for controls on the navy rail — nav items and the panel toggle.
   *  Deliberately not gold: gold already means "the page you're on". */
  hoverOnNavy:  'rgba(255, 255, 255, 0.10)',
} as const;

export const brandFonts = {
  headline: '"Lato", -apple-system, BlinkMacSystemFont, sans-serif',
  body:     '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
} as const;

/**
 * Global CSS that puts the brand headline font on every heading element
 * app-wide. AntD 5 has no per-heading-level `fontFamily` component token for
 * Typography — `token.fontFamily` only reaches body text, and Typography's
 * own component tokens expose just `titleMarginTop`/`titleMarginBottom`
 * (see antd's `typography/style/index.js` `prepareComponentToken`). A plain
 * element-selector rule is therefore the only mechanism that reaches every
 * `Typography.Title` (which renders a native `<h1>`-`<h5>`) — including
 * PageHeader/SectionHeader/page headings built on it — without editing every
 * call site.
 *
 * Scoped to the h1-h5 elements themselves, NOT `.ant-typography` (the class
 * antd puts on Text/Paragraph/Link too, which render as span/div/p) — so
 * body copy stays on `token.fontFamily` (DM Sans) untouched.
 *
 * Mounted once by <ThemeModeProvider>, which both apps already render at the
 * root — no per-app wiring needed.
 */
export const HEADING_FONT_CSS = `h1, h2, h3, h4, h5 { font-family: ${brandFonts.headline}; }`;

/**
 * Theme-aware scrollbar chrome. Native browser scrollbars — the document
 * scrollbar, the app's `overflowY:auto` panels, and antd's internal
 * `.ant-table-*` scroll wrappers — don't respond to antd's theme algorithm, so
 * in dark mode they render the OS-default light-gray against the navy UI. This
 * is one global rule keyed on the `data-theme` attribute <ThemeModeProvider>
 * stamps on <html>. Both engines: Firefox uses the standards `scrollbar-*`
 * props; Chromium/Safari use the `::-webkit-scrollbar` pseudo-elements.
 *
 * Colors are drawn from the palette: light thumb = slate (VENDOIQ_PALETTE.slate
 * #4A4F6A = rgb 74,79,106) at reduced alpha over a transparent track; dark
 * thumb = white at low alpha (0.28, 0.45 on hover), in the `chrome.onNavy*`
 * family. The string is static — both light and dark rules ship at once; only
 * the <html data-theme> attribute changes at runtime.
 */
export const SCROLLBAR_CSS = `
* { scrollbar-width: thin; scrollbar-color: rgba(74, 79, 106, 0.4) transparent; }
*::-webkit-scrollbar { width: 10px; height: 10px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background-color: rgba(74, 79, 106, 0.4); border-radius: 8px; }
*::-webkit-scrollbar-thumb:hover { background-color: rgba(74, 79, 106, 0.6); }
:root[data-theme="dark"], :root[data-theme="dark"] * { scrollbar-color: rgba(255, 255, 255, 0.28) transparent; }
:root[data-theme="dark"]::-webkit-scrollbar-thumb, :root[data-theme="dark"] *::-webkit-scrollbar-thumb { background-color: rgba(255, 255, 255, 0.28); }
:root[data-theme="dark"]::-webkit-scrollbar-thumb:hover, :root[data-theme="dark"] *::-webkit-scrollbar-thumb:hover { background-color: rgba(255, 255, 255, 0.45); }
`;

const P = VENDOIQ_PALETTE;

/**
 * Table header background, by mode. Single source for BOTH the `Table.headerBg`
 * component token below and `TABLE_STICKY_BG_CSS` — keeping one map instead of
 * repeating the ternary means the two can't drift apart.
 */
const TABLE_HEADER_BG = { light: brandColors.honeydew, dark: P.navyDeep };

/**
 * Sticky-header holder / horizontal-scroll holder background, scoped to the
 * Batch leads table (`.viq-batch-leads-table`, `BatchTable`'s own `className`
 * — antd puts it on the `.ant-table-wrapper` root, an ancestor of both).
 *
 * antd paints `.ant-table-sticky-holder` with `colorBgContainer`, and
 * `.ant-table-sticky-scroll` with `colorSplit` — itself alpha-blended FROM
 * `colorBgContainer` (antd's `theme/util/alias.js`). Both are container-level
 * tokens, not the table's own `headerBg` component token. This theme sets
 * `headerBg` to a color DIFFERENT from `colorBgContainer` in both modes (dark:
 * navyDeep vs navy; light: honeydew vs white — see `buildVendoiqTheme` below),
 * so wherever the holder/scroll-holder shows through the header row — around
 * the two `fixed` columns (Name, Captured), cell gaps, borders — the operator
 * sees a lighter band than the header cells around it.
 *
 * `!important`: antd's cssinjs rule (`.css-<hash>.ant-table-wrapper
 * .ant-table-sticky-holder`) carries 3 class selectors of specificity to our
 * 2 (`.viq-batch-leads-table .ant-table-sticky-holder`) and would otherwise
 * win regardless of injection order.
 *
 * Scoped to this one table rather than every `.ant-table` in either app —
 * it's the only sticky-header + fixed-column table today; broaden the
 * selector if that changes. Mounted once by `<ThemeModeProvider>` alongside
 * `SCROLLBAR_CSS`/`HEADING_FONT_CSS` — this repo's one static-CSS injection
 * point — rather than a new stylesheet or a per-render inline style.
 */
export const TABLE_STICKY_BG_CSS = `
.viq-batch-leads-table .ant-table-sticky-holder,
.viq-batch-leads-table .ant-table-sticky-scroll {
  background: ${TABLE_HEADER_BG.light} !important;
}
:root[data-theme="dark"] .viq-batch-leads-table .ant-table-sticky-holder,
:root[data-theme="dark"] .viq-batch-leads-table .ant-table-sticky-scroll {
  background: ${TABLE_HEADER_BG.dark} !important;
}
`;

export function buildVendoiqTheme(mode: 'light' | 'dark'): ThemeConfig {
  const isDark = mode === 'dark';
  const algorithm = isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;
  return {
    algorithm,
    token: {
      // Navy is the brand accent in light. In dark it's too dark to read as a
      // foreground — it equalled the dark container background and hid the
      // pagination active page, links, and active states — so dark mode uses a
      // legible brand blue.
      colorPrimary: isDark ? P.linkBright : brandColors.midnightNavy,
      colorSuccess: brandColors.checkmarkGreen,
      colorWarning: brandColors.signatureGold,
      colorError:   P.coral,
      colorInfo:    isDark ? P.linkBright : P.link,
      // Light keeps bright, near-white surfaces (for viewing in bright spaces).
      // Dark uses the branded navy surfaces that match the header/sider chrome —
      // safe now because the accent is blue (not navy), so foreground elements
      // stay readable on navy. (Navy-on-navy was the original pager bug.)
      colorBgLayout:    isDark ? P.navyDeep : brandColors.honeydew,
      colorBgContainer: isDark ? P.navy     : '#FFFFFF',
      colorTextBase: isDark ? P.cream : P.navyDeep,
      // Slate secondary text reads on light; in dark let the algorithm pick a
      // legible light-gray (slate was too dark on the dark surface).
      ...(isDark ? {} : { colorTextSecondary: P.slate }),
      borderRadius: 8,
      fontFamily: brandFonts.body,
    },
    components: {
      Layout: { headerBg: P.navy, siderBg: P.navyDeep },
      Menu:   {
        darkItemSelectedBg: P.gold,
        darkItemSelectedColor: P.navyDeep,
        // antd's dark-menu hover only brightens the text, which barely reads on
        // navy. The IQ Panel is the only dark menu in either app.
        darkItemHoverBg: chrome.hoverOnNavy,
      },
      Button: { primaryShadow: 'none' },
      // Table density numbers are deliberately duplicated from packages/ui/src/tokens.ts —
      // this file cannot import them because packages/ui depends on packages/theme. The
      // anti-drift test packages/ui/__tests__/tokens.test.ts keeps the two in sync.
      Table: {
        cellPaddingBlock: 12,
        cellPaddingBlockSM: 8,
        cellFontSizeSM: 12,
        cellPaddingInlineSM: 6,
        headerBg: isDark ? TABLE_HEADER_BG.dark : TABLE_HEADER_BG.light,
        // Solid color, NOT rgba: antd's fixed-column overlay relies on this
        // token being fully opaque to cover the scrolled-under cells behind a
        // `fixed: 'left'`/`'right'` column. An alpha channel here makes the
        // Batch worksheet's pinned Name column go see-through on hover while
        // scrolled — antd maintainers confirmed this is inherent to how the
        // token is applied, not fixable on their side (ant-design/ant-design#47212:
        // "Transparent background color will make rows overlap, try to
        // transform it into a solid color (alpha is 1)"). This hex is
        // white-at-6%-opacity pre-blended onto the dark surface (P.navy,
        // #191D5A) — same visual hover tint, opaque.
        rowHoverBg: isDark ? '#272B64' : brandColors.honeydew,
      },
      // Tabs are themed in BOTH modes because the Batch screen's stage strip
      // (Leads / Clean / Match / Send) is `type="card"`. The previous
      // light-only block was correct for antd's default line tabs — in dark,
      // the algorithm and the blue colorPrimary render a readable ink bar on
      // their own. A CARD tab is a different problem: its background comes
      // from `cardBg`, which in dark sits on the navy-deep container it is
      // drawn against, so the selected tab all but disappeared. Gold on navy
      // is the same selected treatment the nav Menu already uses.
      Tabs: isDark
        ? {
            cardBg:            P.navyDeep,
            itemColor:         chrome.onNavyMuted,
            itemHoverColor:    P.cream,
            itemSelectedColor: P.gold,
            inkBarColor:       P.gold,
            titleFontSizeLG:   16,
          }
        : {
            cardBg:            brandColors.honeydew,
            itemSelectedColor: brandColors.midnightNavy,
            inkBarColor:       brandColors.midnightNavy,
            itemHoverColor:    brandColors.midnightNavy,
            titleFontSizeLG:   16,
          },
      // Light-only brand override. In dark the algorithm renders a neutral
      // tag unaided.
      ...(isDark ? {} : { Tag: { defaultBg: P.mintBg, defaultColor: P.navy } }),
    },
  };
}
